import { StickyOffsetProvider, SystemBanner, SystemBannerRegion } from '@if/mui'
import { Box } from '@mui/material'
import { AlertBannerContext, type AlertBannerState } from '@src/contexts/alert-banner-context'
import { type ReactNode, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'

const defaultState: AlertBannerState = { message: null, severity: 'error' }

interface Props {
  children: ReactNode
}

const AlertBannerProvider = ({ children }: Props) => {
  const [banner, setBanner] = useState<AlertBannerState>(defaultState)
  const [bannerHeight, setBannerHeight] = useState(0)
  const regionRef = useRef<HTMLDivElement>(null)

  const showBanner = useCallback((message: string, severity: AlertBannerState['severity'] = 'error') => {
    setBanner({ message, severity })
  }, [])

  const hideBanner = useCallback(() => {
    setBanner(defaultState)
  }, [])

  useLayoutEffect(() => {
    const el = regionRef.current
    if (!el) return
    const update = () => setBannerHeight(el.offsetHeight)
    update()
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const value = useMemo(
    () => ({ banner, bannerHeight, showBanner, hideBanner }),
    [banner, bannerHeight, showBanner, hideBanner],
  )

  return (
    <AlertBannerContext.Provider value={value}>
      <Box ref={regionRef} sx={{ position: 'sticky', top: 0, zIndex: 100 }}>
        <SystemBannerRegion>
          {banner.message !== null && <SystemBanner severity={banner.severity}>{banner.message}</SystemBanner>}
        </SystemBannerRegion>
      </Box>
      <StickyOffsetProvider offset={bannerHeight}>{children}</StickyOffsetProvider>
    </AlertBannerContext.Provider>
  )
}

export default AlertBannerProvider
